<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { RouterLink, RouterView, useRoute } from 'vue-router'
import { ClerkLoaded, ClerkLoading, SignIn, Show, UserButton, useAuth } from '@clerk/vue'
import { useReferenceStore } from '@/stores/reference'
import { API_BASE_URL, setAuthTokenProvider } from '@/api/client'
import NotFoundView from '@/views/NotFoundView.vue'

const reference = useReferenceStore()
const menuOpen = ref(false)

const { isSignedIn, getToken } = useAuth()

/**
 * Hand the API client a way to mint a session token.
 *
 * Registered once here rather than per request: `useAuth` is only valid inside
 * a component's setup, but `api/client.ts` is a plain module. Clerk caches and
 * refreshes the token internally, so calling `getToken` on every request is
 * cheap and never hands back a stale one.
 */
setAuthTokenProvider(async () => (await getToken.value?.()) ?? null)

/**
 * Reference data is fetched once the session exists, not on mount — every API
 * call needs a token, and at mount time Clerk has usually not resolved yet.
 */
watch(
  isSignedIn,
  (signedIn) => {
    if (signedIn) void reference.ensureLoaded()
  },
  { immediate: true },
)

/**
 * Signed in, but Clerk has no role for this account. The API returns 403
 * `no_access` and there is nothing the person can do from here except ask an
 * administrator — so say that plainly rather than showing an empty app.
 */
const notProvisioned = computed(
  () => reference.error !== null && reference.errorCode === 'no_access',
)

/**
 * Admin-only pages are gated here, where they would render.
 *
 * Until the role is known the page waits rather than guessing either way:
 * rendering first would fire its admin API calls and flash admin UI at a
 * client, and refusing first would bounce an admin on every fresh load. A
 * client reaching one gets the ordinary not-found page -- the same answer the
 * API gives for things a client may not see, so neither reveals they exist.
 *
 * The API enforces all of this independently; this is so a client is never
 * shown the page at all, not what keeps the data safe.
 */
const route = useRoute()
const adminOnly = computed(() => route.matched.some((record) => record.meta.adminOnly))
const editorOnly = computed(() => route.matched.some((record) => record.meta.editorOnly))
/** Print views: same sign-in and access rules, none of the app's chrome. */
const bare = computed(() => route.matched.some((record) => record.meta.bare))
const pageAccess = computed<'allowed' | 'pending' | 'denied'>(() => {
  if (!adminOnly.value && !editorOnly.value) return 'allowed'
  if (reference.user) {
    const allowed = adminOnly.value ? reference.user.is_admin : reference.user.can_edit
    return allowed ? 'allowed' : 'denied'
  }
  // No role and no load in progress means the load failed; the banner above
  // explains that, and an admin page should not render without a role.
  return reference.error ? 'denied' : 'pending'
})

// The router titles the tab from the route before this gate runs; a client
// on an admin page should not learn its name from the tab either.
// Watches the path too, so moving between two blocked pages re-applies it,
// and runs post-render so it lands after the router has set its own title.
watch(
  [pageAccess, () => route.fullPath],
  ([access]) => {
    if (access === 'denied') {
      document.title = `Not found - ${import.meta.env.VITE_APP_NAME ?? 'Bedouin'}`
    }
  },
  { flush: 'post' },
)

const NAV = [
  { to: '/', label: 'Dashboard', exact: true },
  { to: '/clients', label: 'Clients' },
  { to: '/projects', label: 'Projects' },
  { to: '/deliverables', label: 'Delivery' },
  { to: '/languages', label: 'Languages' },
  { to: '/casting', label: 'Casting' },
  { to: '/outstanding', label: 'Still needed' },
  { to: '/intake', label: 'Intake', editorOnly: true },
]

/**
 * Clients get the read-only subset. Admin-only entries stay hidden until we
 * positively know the person is an admin, so nothing flashes into view during
 * the first load and then disappears.
 */
const nav = computed(() =>
  NAV.filter((item) => (item.editorOnly ? reference.canEdit : true)),
)
</script>

<template>
  <div class="app-shell">
    <ClerkLoading>
      <div class="auth-splash">
        <p class="auth-splash__brand"><span class="masthead-brand__mark" />Bedouin</p>
        <p class="is-muted is-size-7">Checking your session…</p>
      </div>
    </ClerkLoading>

    <ClerkLoaded>
      <!-- Signed out: nothing but the sign-in card. -->
      <Show when="signed-out">
        <div class="auth-splash">
          <p class="auth-splash__brand"><span class="masthead-brand__mark" />Bedouin</p>
          <p class="auth-splash__tagline">Media post-production tracking</p>
          <SignIn
            routing="hash"
            fallback-redirect-url="/"
            sign-up-fallback-redirect-url="/"
          />
        </div>
      </Show>

      <Show when="signed-in">
        <!-- Signed in but no role assigned yet. -->
        <div v-if="notProvisioned" class="auth-splash">
          <p class="auth-splash__brand"><span class="masthead-brand__mark" />Bedouin</p>
          <div class="notification is-warning auth-splash__notice">
            <p class="mb-2"><strong>Your account is not set up for this workspace yet.</strong></p>
            <p class="is-size-7">
              An administrator needs to assign you a role before you can sign in. If that is you,
              set <code>role</code> in the user's public metadata in the Clerk dashboard.
            </p>
          </div>
          <UserButton />
        </div>

        <template v-else>
          <header v-if="!bare" class="app-masthead">
            <nav class="navbar" role="navigation" aria-label="Main">
              <div class="app-container is-flex-grow-1">
                <div class="navbar-brand">
                  <RouterLink class="navbar-item masthead-brand" to="/">
                    <span class="masthead-brand__mark" aria-hidden="true" />
                    Bedouin
                  </RouterLink>
                  <a
                    role="button"
                    class="navbar-burger"
                    :class="{ 'is-active': menuOpen }"
                    aria-label="Menu"
                    :aria-expanded="menuOpen"
                    @click="menuOpen = !menuOpen"
                  >
                    <span aria-hidden="true" />
                    <span aria-hidden="true" />
                    <span aria-hidden="true" />
                    <span aria-hidden="true" />
                  </a>
                </div>

                <div class="navbar-menu" :class="{ 'is-active': menuOpen }">
                  <div class="navbar-start">
                    <RouterLink
                      v-for="item in nav"
                      :key="item.to"
                      class="navbar-item"
                      :to="item.to"
                      :active-class="item.exact ? '' : 'is-active'"
                      exact-active-class="is-active"
                      @click="menuOpen = false"
                    >
                      {{ item.label }}
                    </RouterLink>
                  </div>

                  <div class="navbar-end">
                    <RouterLink
                      v-if="reference.user?.is_admin"
                      class="navbar-item"
                      to="/users"
                      @click="menuOpen = false"
                    >
                      Users
                    </RouterLink>
                    <RouterLink
                      v-if="reference.user?.is_admin"
                      class="navbar-item"
                      to="/vendors-markets"
                      @click="menuOpen = false"
                    >
                      Vendors &amp; markets
                    </RouterLink>
                    <RouterLink
                      v-if="reference.user?.is_admin"
                      class="navbar-item"
                      to="/settings"
                      @click="menuOpen = false"
                    >
                      Settings
                    </RouterLink>
                    <!-- Clients should know they are on a client account. -->
                    <span v-if="reference.user && !reference.user.is_admin" class="navbar-item">
                      <span
                        class="tag is-status"
                        :title="reference.user.role === 'producer' ? 'You see and edit work for your assigned clients.' : undefined"
                      >
                        {{ reference.user.role === 'producer' ? 'Producer' : 'Client view' }}
                      </span>
                    </span>
                    <div class="navbar-item masthead-user-button">
                      <UserButton />
                    </div>
                  </div>
                </div>
              </div>
            </nav>
          </header>

          <!--
            A failed reference load almost always means the Worker is not
            running, which is the most common local-dev stumble. Name the URL
            the app is actually pointed at and the command that fixes it.
          -->
          <div
            v-if="reference.error && !notProvisioned"
            class="notification is-warning is-radiusless mb-0 py-3"
          >
            <div class="app-container">
              <strong>Cannot reach the API.</strong>
              Tried <code>{{ API_BASE_URL }}</code>. Start it with
              <code>npm run dev:api</code>, or point <code>VITE_API_BASE_URL</code> somewhere else.
            </div>
          </div>

          <main :class="bare ? 'app-bare' : 'app-main'">
            <div :class="{ 'app-container': !bare }">
              <RouterView v-if="pageAccess === 'allowed'" />
              <div v-else-if="pageAccess === 'pending'" class="empty-state">Loading...</div>
              <NotFoundView v-else />
            </div>
          </main>
        </template>
      </Show>
    </ClerkLoaded>
  </div>
</template>
